import React, { ReactElement } from 'react';
import classNames from 'classnames';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import { Snackbar, SnackbarContent, IconButton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { AlyceTheme } from '@alycecom/ui';

const useStyles = makeStyles<AlyceTheme>(({ spacing, palette }) => ({
  content: {
    display: 'flex',
    margin: spacing(1),
    fontSize: spacing(2),
  },
  success: {
    backgroundColor: palette.green.fruitSalad,
  },
  error: {
    backgroundColor: palette.error.light,
  },
  info: {
    backgroundColor: palette.primary.dark,
  },
  warning: {
    backgroundColor: palette.secondary.main,
  },
  close: {
    fontSize: 24,
    '@media (max-width:768px)': {
      fontSize: '14px !important',
    },
  },
  icon: {
    fontSize: 26,
    '@media (max-width:768px)': {
      fontSize: '14px !important',
    },
  },
  iconVariant: {
    opacity: 0.9,
    marginRight: spacing(1),
  },
  message: {
    display: 'flex',
    alignItems: 'center',
    '@media (max-width:768px)': {
      fontSize: '14px !important',
    },
    '@media (max-width:360px)': {
      fontSize: '11px !important',
    },
  },
}));

const variantIcon = {
  success: CheckCircleIcon,
  warning: WarningIcon,
  error: ErrorIcon,
  info: InfoIcon,
};

interface MessageSnackbarProps {
  variant: 'success' | 'warning' | 'error' | 'info';
  message: string;
  isOpened: boolean;
  onClose: (event: React.SyntheticEvent | Event, reason?: string) => void;
}

const MessageSnackbar = ({ variant, message, isOpened, onClose }: MessageSnackbarProps): ReactElement => {
  const classes = useStyles();
  const Icon = variantIcon[variant];

  return (
    <div>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        className={classes.variant}
        open={isOpened}
        onClose={onClose}
        ContentProps={{
          'aria-describedby': 'message-id',
        }}
        autoHideDuration={6000}
      >
        <SnackbarContent
          aria-describedby="client-snackbar"
          className={classNames(classes[variant], classes.content)}
          message={
            <span id="client-snackbar" className={classes.message}>
              <Icon className={classNames(classes.icon, classes.iconVariant)} />
              {message}
            </span>
          }
          action={[
            <IconButton component="button" key="close" color="inherit" onClick={onClose} size="large">
              <CloseIcon className={classes.close} />
            </IconButton>,
          ]}
        />
      </Snackbar>
    </div>
  );
};

export default MessageSnackbar;
